import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import {
  Search, Plus, Pencil, GitFork, ChevronDown, FileCode2, History,
  ShieldAlert, ShieldCheck, FlaskConical, EyeOff, Activity,
} from "lucide-react";
import {
  useSkillsV19, useTransitionSkillStatus, useForkSkillV19,
  ALLOWED_TRANSITIONS, SKILL_STATUSES,
  type SkillV19Row, type SkillStatus,
} from "@/hooks/useSkillsV19";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { SkillV19FormDialog } from "@/components/skills/SkillV19FormDialog";

const STATUS_META: Record<string, { label: string; tone: string; icon: React.ReactNode }> = {
  off:         { label: "Off",         tone: "bg-muted text-muted-foreground",    icon: <EyeOff className="w-3 h-3" /> },
  shadow:      { label: "Shadow",      tone: "bg-warning/20 text-warning",        icon: <FlaskConical className="w-3 h-3" /> },
  on:          { label: "On",          tone: "bg-primary/20 text-primary",        icon: <ShieldCheck className="w-3 h-3" /> },
  deprecated:  { label: "Deprecated",  tone: "bg-destructive/15 text-destructive", icon: <ShieldAlert className="w-3 h-3" /> },
};

const FALLBACK_META = { label: "Unknown", tone: "bg-muted text-muted-foreground", icon: <FileCode2 className="w-3 h-3" /> };
const metaOf = (s: string | null | undefined) => STATUS_META[s ?? ""] ?? FALLBACK_META;

export default function SkillsV19() {
  const { toast } = useToast();
  const { data: orgId } = useCurrentOrgId();
  const { data: skills = [], isLoading } = useSkillsV19();
  const transition = useTransitionSkillStatus();
  const fork = useForkSkillV19();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<SkillStatus | "all">("all");
  const [editing, setEditing] = useState<SkillV19Row | null>(null);
  const [open, setOpen] = useState(false);

  /** Group by slug → keep latest version visible, expose all versions on detail */
  const grouped = useMemo(() => {
    const map = new Map<string, SkillV19Row[]>();
    for (const s of skills) {
      const arr = map.get(s.slug) ?? [];
      arr.push(s);
      map.set(s.slug, arr);
    }
    return Array.from(map.entries()).map(([slug, versions]) => ({
      slug,
      versions: versions.sort((a, b) => b.version - a.version),
      latest: versions[0],
    }));
  }, [skills]);

  const filtered = grouped.filter((g) => {
    if (search) {
      const q = search.toLowerCase();
      if (!g.slug.toLowerCase().includes(q) && !g.latest.name.toLowerCase().includes(q)) return false;
    }
    if (statusFilter !== "all" && !g.versions.some((v) => v.status === statusFilter)) return false;
    return true;
  });

  const counts: Record<SkillStatus, number> = SKILL_STATUSES.reduce((acc, s) => {
    acc[s] = skills.filter((sk) => sk.status === s).length;
    return acc;
  }, {} as Record<SkillStatus, number>);

  const onTransition = async (skill: SkillV19Row, status: SkillStatus) => {
    try {
      await transition.mutateAsync({ id: skill.id, status });
      toast({ title: `${skill.slug} v${skill.version} → ${status}` });
    } catch (e) {
      toast({
        title: "Transição rejeitada",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    }
  };

  const onFork = async (skill: SkillV19Row) => {
    try {
      const created = await fork.mutateAsync(skill);
      toast({ title: `Forked ${created.slug} → v${created.version} (draft)` });
    } catch (e) {
      toast({
        title: "Fork falhou",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    }
  };

  const openCreate = () => { setEditing(null); setOpen(true); };
  const openEdit = (s: SkillV19Row) => { setEditing(s); setOpen(true); };

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Skills" }]} />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-headline">Skills</h1>
          <p className="text-muted-foreground text-sm">
            Memória procedural versionada — slug + version. Lifecycle: off → shadow → on → deprecated.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/skills/legacy"><Button variant="outline">Legacy v3</Button></Link>
          <Link to="/skills/observability"><Button variant="outline"><Activity className="w-4 h-4 mr-1" /> Observability</Button></Link>
          <Button onClick={openCreate}><Plus className="w-4 h-4 mr-1" /> Nova Skill</Button>
        </div>
      </div>

      {/* Status counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {SKILL_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(statusFilter === s ? "all" : s)}
            className={`rounded-md border p-2 text-left transition ${
              statusFilter === s ? "border-primary ring-1 ring-primary" : "border-border hover:bg-muted/40"
            }`}
          >
            <div className="text-xs text-muted-foreground capitalize flex items-center gap-1">
              {STATUS_META[s].icon} {STATUS_META[s].label}
            </div>
            <div className="text-xl font-semibold text-foreground">{counts[s]}</div>
          </button>
        ))}
      </div>

      <SkillV19FormDialog
        open={open}
        onOpenChange={setOpen}
        skill={editing}
        defaultOrgId={orgId ?? null}
      />

      <Tabs defaultValue="list">
        <TabsList>
          <TabsTrigger value="list">Skills ({grouped.length} slugs)</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="space-y-3">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="buscar por slug ou nome..."
                className="pl-8"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {SKILL_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32" />)}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState onCreate={openCreate} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filtered.map(({ slug, versions, latest }) => (
                <SkillCard
                  key={slug}
                  slug={slug}
                  versions={versions}
                  latest={latest}
                  onEdit={openEdit}
                  onTransition={onTransition}
                  onFork={onFork}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SkillCard({
  slug, versions, latest, onEdit, onTransition, onFork,
}: {
  slug: string;
  versions: SkillV19Row[];
  latest: SkillV19Row;
  onEdit: (s: SkillV19Row) => void;
  onTransition: (s: SkillV19Row, status: SkillStatus) => void;
  onFork: (s: SkillV19Row) => void;
}) {
  const allowed = ALLOWED_TRANSITIONS[latest.status] ?? [];
  const meta = metaOf(latest.status);
  const primaryIntent = latest.intent_signature?.primary_intent;
  const secondaryCount = latest.intent_signature?.secondary_intents?.length ?? 0;
  const preCount = latest.pre_conditions?.length ?? 0;
  const postCount = latest.post_conditions?.length ?? 0;
  const isLocked = latest.status === "deprecated";

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <CardTitle className="text-base flex items-center gap-2">
              <span className="truncate">{latest.name}</span>
              <Badge variant="outline" className="text-[10px]">v{latest.version}</Badge>
            </CardTitle>
            <CardDescription className="text-xs font-mono truncate">{slug}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={latest.status === "on"}
              disabled={latest.status === "deprecated"}
              onCheckedChange={(checked) => onTransition(latest, checked ? "on" : "off")}
              aria-label={latest.status === "on" ? "Desativar skill" : "Ativar skill"}
            />
            <Badge className={`gap-1 ${meta.tone}`}>{meta.icon} {meta.label}</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground line-clamp-2">{latest.description || "—"}</p>

        <div className="flex flex-wrap gap-1.5 text-xs">
          {primaryIntent ? (
            <Badge variant="secondary">intent: {primaryIntent}</Badge>
          ) : (
            <Badge variant="destructive" className="text-[10px]">sem intent</Badge>
          )}
          {secondaryCount > 0 && <Badge variant="outline">+{secondaryCount} sec</Badge>}
          {preCount > 0 && <Badge variant="outline">{preCount} pre</Badge>}
          {postCount > 0 && <Badge variant="outline">{postCount} post</Badge>}
          {(latest.tools_allowed?.length ?? 0) > 0 && (
            <Badge variant="outline">{latest.tools_allowed!.length} tools</Badge>
          )}
          {versions.length > 1 && (
            <Badge variant="outline" className="gap-1">
              <History className="w-3 h-3" /> {versions.length} versões
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-1 pt-1">
          <Button size="sm" variant="outline" onClick={() => onEdit(latest)}>
            <Pencil className="w-3 h-3 mr-1" /> {isLocked ? "Ver" : "Editar"}
          </Button>

          <ForkButton skill={latest} onFork={onFork} />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" disabled={allowed.length === 0}>
                Status <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-xs">Promover para</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {allowed.length === 0 && (
                <DropdownMenuItem disabled className="text-xs">
                  Status terminal
                </DropdownMenuItem>
              )}
              {allowed.map((s) => (
                <DropdownMenuItem
                  key={s}
                  onSelect={() => onTransition(latest, s)}
                  className="capitalize text-xs"
                >
                  {STATUS_META[s].icon} <span className="ml-2">{STATUS_META[s].label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {versions.length > 1 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Histórico de versões
            </summary>
            <div className="mt-2 space-y-1">
              {versions.map((v) => (
                <div
                  key={v.id}
                  className="flex items-center justify-between rounded border border-border/50 px-2 py-1"
                >
                  <span className="font-mono">v{v.version}</span>
                  <Badge variant="outline" className={`text-[10px] ${metaOf(v.status).tone}`}>
                    {metaOf(v.status).label}
                  </Badge>
                  <Button size="sm" variant="ghost" onClick={() => onEdit(v)}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

function ForkButton({ skill, onFork }: { skill: SkillV19Row; onFork: (s: SkillV19Row) => void }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" title="Criar nova versão (off)">
          <GitFork className="w-3 h-3 mr-1" /> Fork
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Forkar {skill.slug} v{skill.version}?</AlertDialogTitle>
          <AlertDialogDescription>
            Cria uma nova versão (v{skill.version + 1}) em status <strong>off</strong>, copiando todos
            os campos. Use para iterar sem afetar a versão atual.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={() => onFork(skill)}>Forkar</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <Card className="border-dashed">
      <CardContent className="py-12 flex flex-col items-center text-center">
        <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <FileCode2 className="w-7 h-7 text-primary" />
        </div>
        <h3 className="text-lg font-semibold mb-1">Nenhuma skill ainda</h3>
        <p className="text-sm text-muted-foreground max-w-md mb-6">
          Crie a primeira skill com intent_signature, pré/pós-condições e prompt template.
          A skill nasce em <code>off</code> e pode ser ativada com o toggle quando estiver pronta.
        </p>
        <Button onClick={onCreate}><Plus className="w-4 h-4 mr-1" /> Criar primeira skill</Button>
      </CardContent>
    </Card>
  );
}
